import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

/**
 * MentionInput Component
 * A contentEditable input that supports @mentions with autocomplete
 *
 * @param {Object} props
 * @param {string} props.value - The current text value
 * @param {function} props.onChange - Callback when text changes (text, mentionedUserIds)
 * @param {string} props.placeholder - Placeholder text
 * @param {number} props.rows - Number of rows (for height)
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {function} props.onKeyDown - Additional keydown handler
 */
const MentionInput = forwardRef(({
  value,
  onChange,
  placeholder = 'Type a comment... (@mention someone)',
  rows = 3,
  className = '',
  disabled = false,
  onKeyDown
}, ref) => {
  const { userProfile, company } = useAuth();
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [companyMembers, setCompanyMembers] = useState([]);
  const [mentionMap, setMentionMap] = useState({}); // Maps display names to user IDs
  const inputRef = useRef(null);

  // Expose clear method to parent components
  useImperativeHandle(ref, () => ({
    clear: () => {
      if (inputRef.current) {
        inputRef.current.innerHTML = '';
        setMentionMap({});
      }
    }
  }));

  // Fetch company members for mentions
  useEffect(() => {
    const fetchCompanyMembers = async () => {
      if (!company?.id) return;

      try {
        const { data, error } = await supabase
          .from('company_members')
          .select(`
            user_profiles (
              id,
              first_name,
              last_name,
              display_name,
              avatar_url,
              email
            )
          `)
          .eq('company_id', company.id);

        if (error) throw error;

        const members = data
          ?.map(m => m.user_profiles)
          .filter(Boolean)
          // Filter out current user from mentions
          .filter(m => m.id !== userProfile?.id) || [];

        setCompanyMembers(members);
      } catch (error) {
        console.error('Error fetching company members:', error);
      }
    };

    fetchCompanyMembers();
  }, [company?.id, userProfile?.id]);

  // Initialize contentEditable with value
  useEffect(() => {
    if (inputRef.current && !inputRef.current.textContent && value) {
      inputRef.current.textContent = value;
    }
  }, []);

  const handleInputChange = (e) => {
    const element = e.target;

    // Get text content while preserving mention markers
    let text = '';
    element.childNodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent;
      } else if (node.nodeType === Node.ELEMENT_NODE && node.hasAttribute('data-mention')) {
        // Preserve the mention in the text
        const displayName = node.textContent.substring(1); // Remove @ symbol
        text += `@${displayName}`;
      } else {
        text += node.textContent;
      }
    });

    // Get mentioned user IDs
    const mentionedUserIds = Object.values(mentionMap);

    // Call onChange with both text and mentioned user IDs
    onChange(text, mentionedUserIds, mentionMap);

    // Get cursor position
    const selection = window.getSelection();
    const range = selection?.rangeCount > 0 ? selection.getRangeAt(0) : null;

    let cursorPos = 0;
    if (range) {
      // Calculate the absolute cursor position
      const walker = document.createTreeWalker(
        element,
        NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
        null
      );

      let currentNode;
      while ((currentNode = walker.nextNode())) {
        if (currentNode === range.startContainer) {
          cursorPos += range.startOffset;
          break;
        }

        if (currentNode.nodeType === Node.TEXT_NODE) {
          cursorPos += currentNode.textContent.length;
        } else if (currentNode.nodeType === Node.ELEMENT_NODE && currentNode.hasAttribute('data-mention')) {
          cursorPos += currentNode.textContent.length;
        }
      }
    } else {
      cursorPos = text.length;
    }

    // Check if typing @ for mentions
    const textBeforeCursor = text.substring(0, cursorPos);
    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');

    if (lastAtSymbol !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtSymbol + 1);
      // Only show dropdown if there's no space after @ and we're within 20 chars
      if (!textAfterAt.includes(' ') && textAfterAt.length <= 20) {
        setMentionSearch(textAfterAt.toLowerCase());
        setShowMentionDropdown(true);
      } else {
        setShowMentionDropdown(false);
      }
    } else {
      setShowMentionDropdown(false);
    }
  };

  const insertMention = (member) => {
    const element = inputRef.current;
    if (!element) return;

    // Get the current selection/cursor position
    const selection = window.getSelection();
    const range = selection?.rangeCount > 0 ? selection.getRangeAt(0) : null;
    if (!range) return;

    // Find the text node containing the @ symbol
    let textNode = range.startContainer;
    if (textNode.nodeType !== Node.TEXT_NODE) {
      textNode = textNode.childNodes[range.startOffset];
      if (!textNode || textNode.nodeType !== Node.TEXT_NODE) return;
    }

    const textContent = textNode.textContent || '';
    const cursorOffset = range.startOffset;
    const textBeforeCursor = textContent.substring(0, cursorOffset);
    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');

    if (lastAtSymbol !== -1) {
      const displayName = `${member.first_name} ${member.last_name}`;

      // Store the mapping of display name to user ID
      const newMentionMap = {
        ...mentionMap,
        [displayName]: member.id
      };
      setMentionMap(newMentionMap);

      // Split the text node at the @ symbol
      const beforeAt = textContent.substring(0, lastAtSymbol);
      const afterCursor = textContent.substring(cursorOffset);

      // Create the mention span element
      const mentionSpan = document.createElement('span');
      mentionSpan.className = 'font-semibold text-blue-600 bg-blue-50 px-1 rounded';
      mentionSpan.contentEditable = 'false';
      mentionSpan.setAttribute('data-mention', member.id);
      mentionSpan.textContent = `@${displayName}`;

      // Create text nodes for before and after
      const beforeNode = document.createTextNode(beforeAt);
      const spaceNode = document.createTextNode(' ');
      const afterNode = document.createTextNode(afterCursor);

      // Replace the current text node with the new structure
      const parent = textNode.parentNode;
      parent.insertBefore(beforeNode, textNode);
      parent.insertBefore(mentionSpan, textNode);
      parent.insertBefore(spaceNode, textNode);
      parent.insertBefore(afterNode, textNode);
      parent.removeChild(textNode);

      // Update the text state
      let newText = '';
      element.childNodes.forEach(node => {
        if (node.nodeType === Node.TEXT_NODE) {
          newText += node.textContent;
        } else if (node.nodeType === Node.ELEMENT_NODE && node.hasAttribute('data-mention')) {
          const displayName = node.textContent.substring(1);
          newText += `@${displayName}`;
        } else {
          newText += node.textContent;
        }
      });

      // Get mentioned user IDs
      const mentionedUserIds = Object.values(newMentionMap);

      // Call onChange
      onChange(newText, mentionedUserIds, newMentionMap);

      setShowMentionDropdown(false);
      setMentionSearch('');

      // Set cursor position after the mention
      setTimeout(() => {
        element.focus();
        const newRange = document.createRange();
        const newSel = window.getSelection();
        newRange.setStart(spaceNode, 1);
        newRange.collapse(true);
        newSel?.removeAllRanges();
        newSel?.addRange(newRange);
      }, 0);
    }
  };

  const handleSelectMention = (member) => {
    insertMention(member);
  };

  const handleKeyDown = (e) => {
    // If mention dropdown is open and Enter is pressed, select first option
    if (e.key === 'Enter' && !e.shiftKey && showMentionDropdown) {
      e.preventDefault();

      // Select first filtered member
      const filteredMembers = companyMembers.filter(member => {
        const fullName = `${member.first_name} ${member.last_name}`.toLowerCase();
        return mentionSearch === '' || fullName.includes(mentionSearch);
      });
      if (filteredMembers.length > 0) {
        handleSelectMention(filteredMembers[0]);
      }
      return;
    }

    // Close mention dropdown on Escape
    if (e.key === 'Escape' && showMentionDropdown) {
      e.preventDefault();
      setShowMentionDropdown(false);
      return;
    }

    // Call additional keydown handler if provided
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  // Calculate minimum height based on rows
  const minHeight = rows * 1.5; // Approximate height in rem

  return (
    <div className="relative">
      <style>{`
        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        [contenteditable] {
          outline: none;
        }
        .mention-input-content {
          min-height: ${minHeight}rem;
          max-height: 20rem;
        }
      `}</style>

      <div
        ref={inputRef}
        contentEditable={!disabled}
        onInput={handleInputChange}
        onKeyDown={handleKeyDown}
        className={`mention-input-content w-full overflow-y-auto px-3 py-2 text-sm border border-gray-300 rounded-lg shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''} ${className}`}
        data-placeholder={placeholder}
        style={{
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word'
        }}
      />

      {/* Mention Dropdown */}
      {showMentionDropdown && !disabled && (
        <div className="absolute bottom-full left-0 mb-2 w-full bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
          {companyMembers.length > 0 && companyMembers
            .filter(member => {
              const fullName = `${member.first_name} ${member.last_name}`.toLowerCase();
              return mentionSearch === '' || fullName.includes(mentionSearch);
            })
            .slice(0, 8)
            .map(member => (
              <button
                key={member.id}
                onClick={() => handleSelectMention(member)}
                className="w-full flex items-center space-x-3 p-2 hover:bg-gray-100 transition-colors text-left"
                type="button"
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src={member.avatar_url} alt={`${member.first_name} ${member.last_name}`} />
                  <AvatarFallback>
                    {`${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">
                    {member.first_name} {member.last_name}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{member.email}</p>
                </div>
              </button>
            ))}

          {companyMembers.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500">
              No team members found
            </div>
          )}

          {companyMembers.length > 0 && companyMembers.filter(member => {
            const fullName = `${member.first_name} ${member.last_name}`.toLowerCase();
            return mentionSearch === '' || fullName.includes(mentionSearch);
          }).length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500">
              No members found matching "{mentionSearch}"
            </div>
          )}
        </div>
      )}
    </div>
  );
});

MentionInput.displayName = 'MentionInput';

export default MentionInput;
